// Inspiration card HTML — per-card markup (toggle / thumb / structured description / strength control) for the coverflow workspace. Split from inspiration-workspace.js (1 file 1 duty).

import { store } from '../../core/store.js'
import { DEFAULT_ON } from '../create/cards/state.js'
import { esc, escAttr } from '../../utils/escape.js'

// Normalize a card category for comparison. Bundle cards arrive Title-case
// ('Color'), vision/reference cards lowercase ('color'); every category check
// (DEFAULT_ON, INTENT_FILTER, addReferenceCards) routes through this so the two
// casings never cancel each other's selection. why → Zeus click-path F3 2026-06-21.
/**
 * Lowercase a card category so Title-case bundle cards and lowercase vision
 * cards compare equal.
 * @param {*} c - raw category value
 * @returns {string}
 */
export const catKey = (c) => String(c || '').toLowerCase()

// Card strength labels (ADR-005 2.5.0 — weighted cards, not on/off). The
// backend maps low/high to prompt phrasing ("a touch of" / "strong"); mid =
// verbatim. why → ADR-005 2.5.0.
const CS_LABEL = { low: 'Subtle', mid: 'Medium', high: 'Strong' }

// ─── Structured description renderer ─────────────────────────────────────────

/**
 * Render a structured JSON description as HTML with field tags.
 * @param {object} parsed - Parsed JSON description
 * @returns {string} HTML string
 */
function renderStructuredDesc(parsed) {
  const parts = []
  const fieldOrder = [
    'primary', 'dominant', 'setting',
    'medium', 'type', 'framing',
    'secondary', 'modifiers',
    'accent', 'atmosphere', 'background',
    'technique', 'era', 'rendering',
    'direction', 'intensity', 'shadow_quality',
    'angle', 'dof', 'placement',
    'harmony', 'temperature', 'environment_type',
    'quality', 'negative_space', 'color_temperature',
    'texture_label', 'key',
    'tags',
  ]

  for (const field of fieldOrder) {
    const val = parsed[field]
    if (val == null) continue
    if (typeof val === 'number') continue

    let text
    if (Array.isArray(val)) {
      const filtered = val.filter(v => v && typeof v === 'string')
      if (filtered.length === 0) continue
      text = filtered.join(', ')
    } else if (typeof val === 'string') {
      if (!val.trim()) continue
      text = val
    } else if (typeof val === 'object') {
      continue
    } else {
      continue
    }

    parts.push(`<span class="desc-tag">${esc(text)}</span>`)
  }

  return parts.length > 0 ? parts.join(' ') : `<span class="desc-tag">—</span>`
}

// ─── Card HTML ────────────────────────────────────────────────────────────────

/**
 * Build the inner HTML of one inspiration card (or the trailing Custom card):
 * toggle, category/thumb header, title + structured description, strength
 * segmented control, usage count.
 * @param {Object} card - inspiration card (id 'custom' renders the free-text card)
 * @returns {string} HTML string
 */
export function _cardInnerHTML(card) {
  if (card.id === 'custom') {
    return `
      <label class="card-toggle-top" onclick="event.stopPropagation()">
        <input type="checkbox" data-card-id="custom" />
        <span class="toggle-slider"></span>
      </label>
      <div class="card-main">
        <div class="card-header-row">
          <span class="card-category">Custom</span>
        </div>
        <div class="card-content-box">
          <textarea id="iwCustomPrompt" class="iw-custom-card-textarea" placeholder="Describe your own style..." rows="3" onclick="event.stopPropagation()"></textarea>
        </div>
      </div>`
  }

  const s = store.getState('inspirationCards')
  const checked = s.checkedCards?.[card.id] ?? DEFAULT_ON.has(catKey(card.category))
  const strength = s.cardStrengths?.[card.id] || 'mid'
  const thumb = card.thumbnail || card.thumbnail_url || ''

  // If title and description overlap, only show description (the full prompt)
  const title = (card.title || '').trim()
  const desc = (card.description || '').trim()
  const showTitle = title && title !== desc && !desc.startsWith(title)

  let descHtml
  try {
    const parsed = JSON.parse(desc)
    descHtml = renderStructuredDesc(parsed)
  } catch {
    descHtml = esc(desc)
  }

  return `
    <label class="card-toggle-top" onclick="event.stopPropagation()">
      <input type="checkbox" data-card-id="${card.id}" ${checked ? 'checked' : ''} />
      <span class="toggle-slider"></span>
    </label>
    <div class="card-main">
      <div class="card-header-row">
        <span class="card-category">${esc(card.category || 'Style')}</span>
        ${thumb ? `<img class="card-thumb" src="${escAttr(thumb)}" alt="" loading="lazy" onerror="this.style.display='none'" />` : ''}
      </div>
      <div class="card-content-box">
        ${showTitle ? `<div class="card-content-title">${esc(title)}</div>` : ''}
        <div class="card-content-desc${showTitle ? '' : ' card-content-desc--solo'}">${descHtml}</div>
      </div>
    </div>
    <div class="card-footer">
      <div class="card-strength" data-card-id="${card.id}" title="How strongly to apply this card">
        ${['low', 'mid', 'high'].map(lv => `<button type="button" class="cs-opt${strength === lv ? ' active' : ''}" data-str="${lv}" onclick="event.stopPropagation()">${CS_LABEL[lv]}</button>`).join('')}
      </div>
      <span class="card-usage">
        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/></svg>
        ${card.usage_count || 0}
      </span>
    </div>`
}
